import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { buildPriorityMap } from '../../utilities/locatorDrift/locatorFormatters';
import {
  checkLocatorApiHealth,
  fetchBeaconPriorities,
  fetchDriftIgnoreList,
  fetchLocatorFlows,
} from '../../services/locatorDriftService';
import { errorMessage } from './locatorDriftUtils';

// Start unused Locator Drift scenario runner core state.
// Retained for the non-batch runner flow, although the current LocatorDriftPage UI does not render it.
export const checkLocatorApiHealthAsync = createAsyncThunk(
  'locatorDrift/core/checkHealth',
  async (_, { rejectWithValue }) => {
    try {
      await checkLocatorApiHealth();
      return 'connected';
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

export const fetchLocatorFlowsAsync = createAsyncThunk(
  'locatorDrift/core/fetchFlows',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchLocatorFlows();
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);
// End unused Locator Drift scenario runner core state.

export const loadLocatorPrioritiesAsync = createAsyncThunk(
  'locatorDrift/core/loadPriorities',
  async (_, { rejectWithValue }) => {
    try {
      return buildPriorityMap(await fetchBeaconPriorities());
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

export const loadDriftIgnoreListAsync = createAsyncThunk(
  'locatorDrift/core/loadIgnoreList',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchDriftIgnoreList();
      return {
        locators: Array.isArray(data?.locators) ? data.locators : [],
        beacons: Array.isArray(data?.beacons) ? data.beacons : [],
      };
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

const locatorCoreSlice = createSlice({
  name: 'locatorDriftCore',
  initialState: {
    platform: 'android',
    activeTab: 'results',
    priorities: {},
    ignoreList: { locators: [], beacons: [] },
    // Start unused Locator Drift scenario runner state fields.
    flows: [],
    flowsLoading: false,
    flowsError: null,
    apiStatus: 'checking',
    // End unused Locator Drift scenario runner state fields.
  },
  reducers: {
    // Start unused Locator Drift scenario runner reducer.
    setPlatform(state, action) {
      state.platform = action.payload;
    },
    // End unused Locator Drift scenario runner reducer.
    setActiveTab(state, action) {
      state.activeTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadLocatorPrioritiesAsync.fulfilled, (state, action) => {
        state.priorities = action.payload;
      })
      .addCase(loadDriftIgnoreListAsync.fulfilled, (state, action) => {
        state.ignoreList = action.payload;
      })
      // Start unused Locator Drift scenario runner extra reducers.
      .addCase(checkLocatorApiHealthAsync.fulfilled, (state) => {
        state.apiStatus = 'connected';
      })
      .addCase(checkLocatorApiHealthAsync.rejected, (state) => {
        state.apiStatus = 'offline';
      })
      .addCase(fetchLocatorFlowsAsync.pending, (state) => {
        state.flowsLoading = true;
        state.flowsError = null;
      })
      .addCase(fetchLocatorFlowsAsync.fulfilled, (state, action) => {
        state.flowsLoading = false;
        state.flows = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchLocatorFlowsAsync.rejected, (state, action) => {
        state.flowsLoading = false;
        state.flowsError = action.payload;
      });
    // End unused Locator Drift scenario runner extra reducers.
  },
});

export const { setActiveTab, setPlatform } = locatorCoreSlice.actions;
export default locatorCoreSlice.reducer;
